/** @flow */

import { takeLatest, put, all, call } from "redux-saga/effects";
import http from "@http";
import config from "@config";
import { push } from "connected-react-router";
import { route } from "@routes/helper";
import type { Action } from "redux-action";
import {
  GetReportingFocusListsDepartmentReport,
  GetFocusListsDepartmentReportSchema,
  CreateFocusListsDepartmentSavedReport,
  UpdateFocusListsDepartmentSavedReport,
  DeleteFocusListsDepartmentSavedReport,
  ExportFocusListsDepartmentReport,
  ExportFocusListsDepartmentReportSuccess,
  GetFocusListDepartmentReportProfile,
} from "@modules/reporting/constants/actions";
import actionCreator from "@modules/reporting/actionCreator";
import type {
  DepartmentReport,
  EffectiveParameters,
} from "@modules/reporting/reducer/focus-lists/department/types";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import actionCreators from "@modules/reporting/actionCreator";
import { downloadCsvFile } from "@sagas/utils";
import { AppEvent, fireEvent, prepareEvent } from "@modules/app-events";
import { parseSchema } from "@utils/schema";

export function* getEffectiveAbsenceParameters(
  clientId: number,
): Generator<*, EffectiveParameters | null, { data: EffectiveParameters }> {
  try {
    if (!clientId) {
      throw Error("Invalid clientId provided");
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${clientId}/service-config/absence-management/reporting-parameters`,
    );

    return response.data;
  } catch (e) {
    /**
     * For customer without paid or configured Effective Absence Management the response code is 400.
     */
    yield put(
      fireEvent(
        prepareEvent(AppEvent.error(), {
          message: e.message,
        }),
      ),
    );
    return null;
  }
}

type ReportDataRequest = $Exact<{
  clientId: number,
  report: DepartmentReport,
  noRedirect?: boolean,
}>;

function* onGetReportingFocusListsDepartmentReport({
  payload,
}: Action<ReportDataRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.report) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const [reportResonse, effectiveParameters] = yield all([
      call(
        http.post,
        `/api/v1/focus-lists/client/${payload.clientId}/focus-lists/organisational-unit`,
        payload.report.filters,
      ),
      call(getEffectiveAbsenceParameters, payload.clientId),
    ]);

    yield put(
      actionCreator.GetReportingFocusListsDepartmentReportSuccess({
        effectiveParameters,
        details: payload.report,
        reportData: reportResonse.data.report.content,
        focusList: reportResonse.data.charts,
      }),
    );

    if (!payload.noRedirect) {
      yield put(
        push(route("REPORTING_FOCUS_LISTS_DEPARTMENT_REPORT", { clientId: payload.clientId })),
      );
    }
  } catch (e) {
    yield put(
      actionCreator.GetReportingFocusListsDepartmentReportError({
        error: e.message,
      }),
    );
  }
}

type ReportSchemaRequest = $Exact<{
  clientId: number,
}>;

export function* onGetFocusListsDepartmentReportSchema({
  payload,
}: Action<ReportSchemaRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const schema = yield parseSchema("OrganisationalUnitFocusListReportFiltersDTO", {
      clientId: payload.clientId,
    });

    yield put(actionCreators.GetFocusListsDepartmentReportSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.GetFocusListsDepartmentReportSchemaError(e.response));
  }
}

type CreateFocusListsDepartmentSavedReportRequest = $Exact<{
  clientId: number,
  filters: Object,
  columns: string[],
  name: string,
}>;

export function* onCreateFocusListsDepartmentSavedReport({
  payload,
}: Action<CreateFocusListsDepartmentSavedReportRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.filters || !payload.columns || !payload.name) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.post,
      `/api/v1/focus-lists/client/${payload.clientId}/focus-lists/organisational-unit/my-saved-reports`,
      {
        name: payload.name,
        filters: payload.filters,
        columns: payload.columns,
      },
    );

    yield all([
      put(actionCreators.CreateFocusListsDepartmentSavedReportSuccess(response.data)),
      put(actionCreators.ToggleSaveReportModal({ visible: false })),
      put(actionCreators.GetReportingFocusListsDepartmentMyReports({ clientId: payload.clientId })),
      put(
        actionCreators.GetReportingFocusListsDepartmentReport({
          clientId: payload.clientId,
          report: payload,
        }),
      ),
      put(sendSuccessMessage("REPORT_CREATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.CreateFocusListsDepartmentSavedReportError(e.response));
  }
}

type UpdateFocusListsDepartmentSavedReportRequest = $Exact<{
  clientId: number,
  reportId: number,
  data: Object,
  shouldFetchReportData?: boolean,
}>;

export function* onUpdateFocusListsDepartmentSavedReport({
  payload,
}: Action<UpdateFocusListsDepartmentSavedReportRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.reportId || !payload.data) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.put,
      `/api/v1/focus-lists/client/${payload.clientId}/focus-lists/organisational-unit/my-saved-reports/${payload.reportId}`,
      payload.data,
    );

    const additionalActions = payload.shouldFetchReportData
      ? [
          put(
            actionCreators.GetReportingFocusListsDepartmentReport({
              clientId: payload.clientId,
              report: response.data,
              noRedirect: true,
            }),
          ),
        ]
      : [];

    yield all([
      put(actionCreators.UpdateFocusListsDepartmentSavedReportSuccess(response.data)),
      put(actionCreators.ToggleSaveReportModal({ visible: false })),
      put(actionCreators.GetReportingFocusListsDepartmentMyReports({ clientId: payload.clientId })),
      put(sendSuccessMessage("REPORT_UPDATED")),
      ...additionalActions,
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateFocusListsDepartmentSavedReportError(e.response));
  }
}

type DeleteFocusListsDepartmentSavedReportRequest = $Exact<{
  clientId: number,
  reportId: number,
  successMessage: string,
}>;

export function* onDeleteFocusListsDepartmentSavedReport({
  payload,
}: Action<DeleteFocusListsDepartmentSavedReportRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.reportId || !payload.successMessage) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    yield call(
      http.delete,
      `/api/v1/focus-lists/client/${payload.clientId}/focus-lists/organisational-unit/my-saved-reports/${payload.reportId}`,
    );

    yield all([
      put(actionCreators.DeleteFocusListsDepartmentSavedReportSuccess()),
      put(actionCreators.GetReportingFocusListsDepartmentMyReports({ clientId: payload.clientId })),
      put(sendSuccessMessage(payload.successMessage)),
    ]);
  } catch (e) {
    yield put(actionCreators.DeleteFocusListsDepartmentSavedReportError(e.response));
  }
}

export function* onExportFocusListsDepartmentReport({
  payload,
}: Action<ReportDataRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.report) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.post,
      `/api/v1/focus-lists/client/${payload.clientId}/focus-lists/organisational-unit/export`,
      { filters: payload.report.filters, columns: payload.report.columns },
      {
        headers: {
          Accept: "text/csv",
        },
      },
    );

    yield put(actionCreators.ExportFocusListsDepartmentReportSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.ExportFocusListsDepartmentReportError(e.response));
  }
}

export function* onExportFocusListsDepartmentReportSuccess({
  payload,
}: Action<any>): Generator<*, void, any> {
  yield downloadCsvFile(payload, "focus-lists-department-report.csv");
}

type DepartmentProfileRequest = $Exact<{
  clientId: string,
  departmentId: number,
  filters: Object,
}>;

function* onGetFocusListDepartmentReportProfile({
  payload,
}: Action<DepartmentProfileRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.departmentId || !payload.filters) {
      throw Error("Invalid payload provider");
    }

    const response = yield call(
      http.post,
      `${config.endpointBaseUrl}api/v1/focus-lists/client/${payload.clientId}/focus-lists/organisational-unit/${payload.departmentId}/absence-profile`,
      payload.filters,
    );

    yield put(actionCreators.GetFocusListDepartmentReportProfileSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetFocusListDepartmentReportProfileError(e.response));
  }
}

export default function* reportSaga(): Generator<*, void, void> {
  yield all([
    takeLatest(GetReportingFocusListsDepartmentReport, onGetReportingFocusListsDepartmentReport),
    takeLatest(GetFocusListsDepartmentReportSchema, onGetFocusListsDepartmentReportSchema),
    takeLatest(CreateFocusListsDepartmentSavedReport, onCreateFocusListsDepartmentSavedReport),
    takeLatest(UpdateFocusListsDepartmentSavedReport, onUpdateFocusListsDepartmentSavedReport),
    takeLatest(DeleteFocusListsDepartmentSavedReport, onDeleteFocusListsDepartmentSavedReport),
    takeLatest(ExportFocusListsDepartmentReport, onExportFocusListsDepartmentReport),
    takeLatest(ExportFocusListsDepartmentReportSuccess, onExportFocusListsDepartmentReportSuccess),
    takeLatest(GetFocusListDepartmentReportProfile, onGetFocusListDepartmentReportProfile),
  ]);
}
